import { IProductCard, IProductCardDataAction, IProductCardData } from 'models/product/productcard';
import { INodeDto, IEntityDto } from 'models/common';
import { IStore } from 'redux/IStore';
import { IUserPreferences } from 'models/user/userpreferences';
import { Wishlists } from 'redux/modules/user/wishlists';
import { WishlistNodeType } from 'models/enums/nodetype/wishlistnodetype';
import { LikeNodeType } from 'models/enums/nodetype/likenodetype';
import { Likes } from 'redux/modules/user/likes';
import { IShoppingBasket } from 'models/cart/shoppingbasket';
import { browserHistory } from 'react-router';

/** Cart Actions */
import { handleShoppingBasketSuccess, resetShoppingBasketShippingOptions } from '../../cart';

/** Error Actions */
import { goToErrorPage } from '../../error';
import { accessTokenIsPresent } from 'containers/AccessControl/helpers';

/** Action Types */
export const REQUEST_START: string = 'productcard/REQUEST_START';
export const GET_SUCCESS: string = 'productcard/GET_SUCCESS';
export const REQUEST_FAILURE: string = 'productcard/REQUEST_FAILURE';
export const SET_SHOP_IS_FOLLOWED: string = 'productcard/SET_SHOP_IS_FOLLOWED';
export const SET_IS_MY_SHOP: string = 'productcard/SET_IS_MY_SHOP';
export const FOLLOW_SHOP_SUCCESS: string = 'productcard/FOLLOW_SHOP_SUCCESS';
export const UNFOLLOW_SHOP_SUCCESS: string = 'productcard/UNFOLLOW_SHOP_SUCCESS';
export const FOLLOW_SHOP_START: string = 'productcard/FOLLOW_SHOP_START';
export const GET_PRODUCT_LIKERS_SUCCESS: string = 'productcard/GET_PRODUCT_LIKERS_SUCCESS';
export const RESET_DATA: string = 'productcard/RESET_DATA';
export const LIKE_ITEM_SUCCESS: string = 'productcard/LIKE_ITEM_SUCCESS';
export const WISHLIST_ITEM_SUCCESS: string = 'productcard/WISHLIST_ITEM_SUCCESS';
export const EDITORCHOICE_ITEM_SUCCESS: string = 'productcard/EDITORCHOICE_ITEM_SUCCESS';
export const GET_PRODUCT_VARIATIONS_SUCCESS: string = 'productcard/GET_PRODUCT_VARIATIONS_SUCCESS';

/** Initial State */
const initialState: IProductCardData = {
	isFetching: false,
	productCard: null,
	youMayAlsoLikeItems: [],
	isMyShop: false,
	productLikers: [],
	productVariations: [],
};

/** Reducer */
export function productCardReducer(state = initialState, action: IProductCardDataAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_SUCCESS:
			return Object.assign({}, state, {
				productCard: action.payload.productCard,
				isFetching: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_SHOP_IS_FOLLOWED:
			return Object.assign({}, state, {
				shopIsFollowed: action.payload.shopIsFollowed,
			});

		case SET_IS_MY_SHOP:
			return Object.assign({}, state, {
				isMyShop: action.payload.isMyShop,
			});

		case FOLLOW_SHOP_SUCCESS:
			return Object.assign({}, state, {
				shopIsFollowed: true,
			});

		case UNFOLLOW_SHOP_SUCCESS:
			return Object.assign({}, state, {
				shopIsFollowed: false,
			});

		case FOLLOW_SHOP_START:
			return Object.assign({}, state, {
				shopIsFollowed: undefined,
			});

		case GET_PRODUCT_LIKERS_SUCCESS:
			return Object.assign({}, state, {
				productLikers: action.payload.productLikers,
				isFetching: false,
			});

		case LIKE_ITEM_SUCCESS:
		case WISHLIST_ITEM_SUCCESS:
		case EDITORCHOICE_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
			});

		case RESET_DATA:
			return Object.assign({}, state, initialState);

		case GET_PRODUCT_VARIATIONS_SUCCESS:
			return Object.assign({}, state, {
				productVariations: action.payload.productVariations,
				isFetching: false,
			});

		default:
			return state;
	}
}

/** Async Action Creator */
export function getProductCardByCodeAndShopId(code: string, shopId: string) {
	return (dispatch, getState: () => IStore) => {
		const userPreferences: IUserPreferences = getState().userPreferences;

		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Product/' + code + '/GetByCode' + '?shopId=' + shopId,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IProductCard) => {
						setProductCardData(res, userPreferences, dispatch);
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function getProductCard(id: string, target?: string) {
	return (dispatch, getState: () => IStore) => {
		const userPreferences: IUserPreferences = getState().userPreferences;

		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Product/' + id,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IProductCard) => {
						setProductCardData(res, userPreferences, dispatch);

						const url = `/products/${res.code}?shopId=${res.shopId}`;
						if (target && target.length > 0) {
							window.open(url, target);
						} else {
							browserHistory.push(url);
						}
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(goToErrorPage(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(goToErrorPage(message));
						});
				}
			})
			.catch((err) => {
				dispatch(goToErrorPage(err));
			});
	};
}

function setProductCardData(res: IProductCard, userPreferences: IUserPreferences, dispatch) {
	dispatch(getProductCardSuccess(res));

	const wishlists = new Wishlists(WishlistNodeType.ProductCard);
	const likes = new Likes(LikeNodeType.ProductCard);

	if (userPreferences && userPreferences.id) {
		if (userPreferences.id && userPreferences.id === res.shopOwnerId) {
			dispatch(setIsMyShop(true));
		} else {
			dispatch(getShopFollowButtonData());
		}
		dispatch(wishlists.getWishlistedItems());
		dispatch(likes.getLikedItems());
	} else {
		dispatch(setShopIsFollowed(false));
		dispatch(likes.updateLikeItems(null));
		dispatch(wishlists.updateWishlistItems(null));
	}
}

/** Async Action Creator */
export function getProductVariations(variantParentId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Product/ProductVariants/' + variantParentId,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IProductCard[]) => {
						dispatch(getProductVariationsSuccess(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getShopFollowButtonData() {
	return (dispatch, getState: () => IStore) => {
		if (!accessTokenIsPresent()) {
			return Promise.resolve(false);
		}
		dispatch(requestStart());

		const entityIds: IEntityDto[] = [];
		entityIds.push({ entityId: getState().productCard.productCard.shopEntityId });

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/Following',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(entityIds),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => dispatch(setShopIsFollowed(res[0].value)));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function followShop(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		dispatch(followShopStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/shop/' + id + '/follow',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
			}),
		)
			.then((res) => {
				if (res.ok || res.status === 409) {
					dispatch(followShopSuccess());
					dispatch(updateShopFollowersCount());
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function unfollowShop(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		dispatch(followShopStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/shop/' + id + '/follow',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		)
			.then((res) => {
				if (res.ok || res.status === 409) {
					dispatch(unfollowShopSuccess());
					dispatch(updateShopFollowersCount(true));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function getProductLikers(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Product/' + id + '/Likers',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: INodeDto[]) => {
						dispatch(getProductLikersSuccess(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function addItemToShoppingBasket(id: string) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());

		return dispatch(resetShoppingBasketShippingOptions())
			.catch((e) => {
				console.warn(e);
			})
			.then(() =>
				fetch(
					getState().fetchDefault.baseAPI + 'api/ShoppingBasket/AddItem/' + id,
					Object.assign({}, getState().fetchDefault.requestInit, {
						method: 'POST',
					}),
				),
			)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IShoppingBasket) => {
						dispatch(handleShoppingBasketSuccess(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function updateShopFollowersCount(isUnfollow: boolean = false) {
	return (dispatch, getState: () => IStore) => {
		const productCard: IProductCard = getState().productCard.productCard;
		if (isUnfollow) {
			productCard.shopFollowersCount--;
		} else {
			productCard.shopFollowersCount++;
		}
		dispatch(getProductCardSuccess(productCard));
	};
}

/** Action Creator */
export function requestStart(): IProductCardDataAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getProductCardSuccess(productCard: IProductCard): IProductCardDataAction {
	return {
		type: GET_SUCCESS,
		payload: {
			productCard,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IProductCardDataAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setShopIsFollowed(shopIsFollowed: boolean): IProductCardDataAction {
	return {
		type: SET_SHOP_IS_FOLLOWED,
		payload: {
			shopIsFollowed,
		},
	};
}

/** Action Creator */
export function setIsMyShop(isMyShop: boolean): IProductCardDataAction {
	return {
		type: SET_IS_MY_SHOP,
		payload: {
			isMyShop,
		},
	};
}

/** Action Creator */
export function followShopSuccess(): IProductCardDataAction {
	return {
		type: FOLLOW_SHOP_SUCCESS,
	};
}

/** Action Creator */
export function unfollowShopSuccess(): IProductCardDataAction {
	return {
		type: UNFOLLOW_SHOP_SUCCESS,
	};
}

/** Action Creator */
export function followShopStart(): IProductCardDataAction {
	return {
		type: FOLLOW_SHOP_START,
	};
}

/** Action Creator */
export function getProductLikersSuccess(productLikers: INodeDto[]): IProductCardDataAction {
	return {
		type: GET_PRODUCT_LIKERS_SUCCESS,
		payload: {
			productLikers,
		},
	};
}

/** Action Creator */
export function resetData(): IProductCardDataAction {
	return {
		type: RESET_DATA,
	};
}

/** Action Creator */
export function likeItemSuccess(): IProductCardDataAction {
	return {
		type: LIKE_ITEM_SUCCESS,
	};
}

/** Action Creator */
export function wishlistItemSuccess(): IProductCardDataAction {
	return {
		type: WISHLIST_ITEM_SUCCESS,
	};
}

/** Action Creator */
export function editorChoiceItemSuccess(): IProductCardDataAction {
	return {
		type: EDITORCHOICE_ITEM_SUCCESS,
	};
}

/** Action Creator */
export function getProductVariationsSuccess(productVariations: IProductCard[]): IProductCardDataAction {
	return {
		type: GET_PRODUCT_VARIATIONS_SUCCESS,
		payload: {
			productVariations,
		},
	};
}
